import type { ScreenView } from "@typedoc/contracts/capture";
import {
  recordingTapeFrameSchema,
  type RecordingTape,
  type RecordingTapeFrame,
  type RecordingView,
} from "@typedoc/contracts/recordings";
import type {
  AddNoteInput,
  CorrectAnswerInput,
  RemovePartInput,
} from "@typedoc/contracts/recordings";
import type { RecordingPackage } from "@typedoc/contracts/recordings/package";
import type { TapeEntry } from "@typedoc/interview/events";
import { absoluteApiBase } from "../contract-client";
import { contractRequest, type ContractCall } from "../contract-request";
import type { RegionalOptions } from "../regional-request";
import { recordingsApiClient, type RecordingsApiClient } from "../rpc/recordings";
import { framesUntil, sleep } from "../stream-frames";
import type { IslandPort } from "./port";

/** A call to the Recordings API is answered at once or not at all. */
const CALL_TIMEOUT_MS = 15_000;
/** The server ends a stream within 55 seconds; one open longer than this is dead, not quiet. */
const MAX_STREAM_MS = 65_000;
/** After a stream fails, the wait before the next try: doubled each time, up to the most. */
const RETRY_FIRST_MS = 1_000;
const RETRY_MOST_MS = 15_000;

export type LiveRecordingsConfig = {
  userId: string;
  workspaceId: string;
  options: RegionalOptions;
  api?: RecordingsApiClient;
  maxStreamMs?: number;
  /** Waits between tries after a stream failed; tests pass one that does not wait. */
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
};

/**
 * Recordings through the Recordings API, for this person in this workspace: the same transport as
 * Chats and Files (the workspace's regional API and a token held in memory), so a change of person
 * or workspace stops what is in flight.
 */
export function createLiveRecordings(config: LiveRecordingsConfig) {
  const { userId, workspaceId, options } = config;
  const api = config.api ?? recordingsApiClient;
  const maxStreamMs = config.maxStreamMs ?? MAX_STREAM_MS;
  const wait = config.wait ?? sleep;
  const scope = { userId, workspaceId };

  const request = <T>(
    signal: AbortSignal | undefined,
    call: ContractCall<RecordingsApiClient, T>,
  ) =>
    contractRequest(
      api,
      scope,
      {
        ...options,
        timeoutMs: options.timeoutMs ?? CALL_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      },
      call,
    );

  /** One stream of the recording's frames from `after`, until it ends or `signal` is aborted. */
  async function openFrames(recordingId: string, after: number, signal: AbortSignal) {
    const { iterator, scopeSignal } = await request(signal, async (c, ws, call, access) => {
      const frames: AsyncIterable<unknown> = await c.stream(
        { params: { workspaceId: ws, recordingId }, query: { after } },
        call,
      );
      return {
        iterator: frames[Symbol.asyncIterator](),
        scopeSignal: access.signal,
      };
    });
    return framesUntil(iterator, signal, scopeSignal, maxStreamMs);
  }

  /**
   * Follows a recording's stream until `signal` is aborted: each frame to `onFrame`, and
   * `reconnected` each time a stream is opened again after the first, from `cursor()`, the last
   * position the caller has. A stream the server ended is opened again at once; one that failed
   * (a frame the contract refuses fails it too), after a wait that grows. A change of person or
   * workspace ends following for good.
   */
  async function follow(
    recordingId: string,
    cursor: () => number,
    onFrame: (frame: RecordingTapeFrame) => void,
    signal: AbortSignal,
    reconnected: () => void = () => {},
  ): Promise<void> {
    let delay = RETRY_FIRST_MS;
    let opened = 0;
    while (!signal.aborted) {
      try {
        const frames = await openFrames(recordingId, cursor(), signal);
        if (opened++ > 0) reconnected();
        for await (const raw of frames) {
          // The contract's client has checked it; this keeps the type honest.
          const frame = recordingTapeFrameSchema.safeParse(raw);
          if (frame.success) onFrame(frame.data);
          delay = RETRY_FIRST_MS;
        }
      } catch (error) {
        if (signal.aborted) return;
        if ((error as { code?: string }).code === "ACCESS_CHANGED") return;
        await wait(delay, signal);
        delay = Math.min(delay * 2, RETRY_MOST_MS);
      }
    }
  }

  /**
   * The island's port onto one recording. The stream carries new entries as they land; they wait
   * here until the island reads them, so a read after the stream's last entry is answered without
   * asking. A read the stream cannot answer (the island is behind it, or the stream has not
   * started) is the `tape?after=` catch-up, as is the read after a reconnect.
   */
  function open(recordingId: string): IslandPort {
    const on = (ws: string) => ({ workspaceId: ws, recordingId });
    /** Entries the stream sent that the island has not read, in order and without gaps. */
    let held: TapeEntry[] = [];
    /** The last position the island has read, or the stream has sent. */
    let known = 0;
    const hold = (entries: TapeEntry[]) => {
      const first = entries[0];
      if (first === undefined) return;
      const last = held.at(-1);
      // Not the next entry after what is held: start again from these.
      held =
        last === undefined || first.seq === last.seq + 1 ? [...held, ...entries] : [...entries];
      known = Math.max(known, entries.at(-1)?.seq ?? known);
    };
    return {
      async read(after, signal) {
        const from = held.findIndex((e) => e.seq === after + 1);
        if (from !== -1) {
          const entries = held.slice(from);
          held = [];
          return { entries, more: false };
        }
        held = held.filter((e) => e.seq > after);
        const page = await request(signal, (c, ws, call) =>
          c.tape({ params: on(ws), query: { after } }, call),
        );
        known = Math.max(known, page.entries.at(-1)?.seq ?? after);
        return { entries: page.entries, more: page.more };
      },
      async send(key, event) {
        const sent = await request(undefined, (c, ws, call) =>
          c.send({ params: on(ws), body: { key, event } }, call),
        );
        return { seq: sent.seq, duplicate: sent.duplicate };
      },
      async discard(signal) {
        await request(signal, (c, ws, call) =>
          c.remove({ params: { workspaceId: ws, recordingId } }, call),
        );
      },
      // Issued by the workspace's regional API, the one the screens go to: its base comes with it.
      async grant(signal) {
        return request(signal, async (c, ws, call) => {
          const grant = await c.grant({ params: on(ws) }, call);
          return {
            recordingId,
            workspaceId: ws,
            apiBase: absoluteApiBase(call.context.base),
            grant,
          };
        });
      },
      voiceJoin: (signal, later) =>
        request(signal, (c, ws, call) =>
          c.voiceJoin({ params: on(ws), query: later ? { listen: "later" } : {} }, call),
        ),
      watch(changed) {
        const stop = new AbortController();
        void follow(
          recordingId,
          () => known,
          (frame) => {
            hold(frame.entries);
            if (frame.entries.length > 0) changed();
          },
          stop.signal,
          // The catch-up after a reconnect: whatever landed while it was down is read.
          changed,
        );
        return () => stop.abort();
      },
    };
  }

  return {
    open,
    /**
     * Follows a recording's view (its state and last position) from `after` until `signal` is
     * aborted: each change to `changed`, with the entries that came with it.
     */
    watchRecording(
      recordingId: string,
      after: number,
      changed: (recording: RecordingView, entries: TapeEntry[]) => void,
      signal: AbortSignal,
    ): Promise<void> {
      let cursor = after;
      return follow(
        recordingId,
        () => cursor,
        (frame) => {
          cursor = Math.max(cursor, frame.entries.at(-1)?.seq ?? cursor);
          changed(frame.recording, frame.entries);
        },
        signal,
      );
    },
    /** The screens kept during a recording, oldest first. Their images are read through Files. */
    async screens(recordingId: string, signal?: AbortSignal): Promise<ScreenView[]> {
      const listed = await request(signal, (c, ws, call) =>
        c.screens({ params: { workspaceId: ws, recordingId } }, call),
      );
      return listed.screens;
    },
    /** The recording's package (ENG-1028): its tape, screens and trace on one clock. */
    async package(recordingId: string, signal?: AbortSignal): Promise<RecordingPackage> {
      return request(signal, (c, ws, call) =>
        c.package({ params: { workspaceId: ws, recordingId } }, call),
      );
    },
    /**
     * Changes an ended recording the person made (ENG-1029): a note at a moment, a corrected
     * answer, or a part or a stretch removed. The answer is the package as it now folds.
     */
    async amend(
      recordingId: string,
      change: Amendment,
      signal?: AbortSignal,
    ): Promise<RecordingPackage> {
      return request(signal, (c, ws, call) => {
        const params = { workspaceId: ws, recordingId };
        switch (change.kind) {
          case "note":
            return c.addNote({ params, body: change.input }, call);
          case "correct":
            return c.correctAnswer({ params, body: change.input }, call);
          case "remove":
            return c.removePart({ params, body: change.input }, call);
        }
      });
    },
    /** Deletes an ended recording the person made, with its tape. */
    async remove(recordingId: string, signal?: AbortSignal): Promise<void> {
      await request(signal, (c, ws, call) =>
        c.remove({ params: { workspaceId: ws, recordingId } }, call),
      );
    },
    /** The chat's recordings, oldest first. */
    async list(threadId: string, signal?: AbortSignal): Promise<RecordingView[]> {
      const listed = await request(signal, (c, ws, call) =>
        c.list({ params: { workspaceId: ws }, query: { threadId } }, call),
      );
      return listed.recordings;
    },
    /**
     * Starts a recording on a chat the person owns. `requestId` makes a resend answer the same
     * recording. The answer carries the first page of its tape and the port onto it.
     */
    async create(
      input: {
        threadId: string;
        intent?: string;
        voice?: boolean;
        requestId: string;
      },
      signal?: AbortSignal,
    ): Promise<{ tape: RecordingTape; port: IslandPort }> {
      const tape = await request(signal, (c, ws, call) =>
        c.create({ params: { workspaceId: ws }, body: input }, call),
      );
      return { tape, port: open(tape.recording.id) };
    },
  };
}

/** One change to an ended recording (ENG-1029), as the package view makes it. */
export type Amendment =
  | { kind: "note"; input: AddNoteInput }
  | { kind: "correct"; input: CorrectAnswerInput }
  | { kind: "remove"; input: RemovePartInput };
